
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { InventoryItem } from '@/types/inventory';

// Verify Supabase connection by fetching sample products
export const verifySuapabaseConnection = async () => {
  try {
    console.log('Verifying Supabase connection by fetching sample products from voorraad_live2...');
    const { data: items, error } = await supabase
      .from('voorraad_live2')
      .select('*')
      .limit(10);

    if (error) {
      console.error('Error connecting to Supabase:', error);
      toast.error('Er kon geen verbinding worden gemaakt met de database');
      throw error;
    }

    console.log('Connection verified! Sample products:', items);
    
    if (items && items.length > 0) {
      // Get timestamp from first record, if available
      const lastUpdate = items[0].timestamp_sync || null;
      if (lastUpdate) {
        console.log('Last update timestamp:', lastUpdate);
      }
      
      // Log raw data structure for debugging
      console.log('Sample data structure - first item:', {
        magazijn: items[0].magazijn,
        magazijnType: typeof items[0].magazijn
      });
      
      // Transform the sample results
      const formattedSamples = formatInventoryItems(items);
      
      toast.success(`Verbinding met database gelukt! ${items.length} producten gevonden.`);
      return { samples: formattedSamples, lastUpdate };
    } else {
      console.log('No sample products found');
      toast.warning('Geen voorbeeldproducten gevonden in de voorraad_live2 tabel');
      return { samples: [], lastUpdate: null };
    }
  } catch (error) {
    console.error('Error verifying connection:', error);
    toast.error('Fout bij het controleren van de databaseverbinding');
    return { samples: [], lastUpdate: null };
  }
};

// Smart word prioritization - longer, more specific words get higher priority
const prioritizeSearchWords = (searchTerm: string): { primaryWord: string; secondaryWords: string[] } => {
  const words = searchTerm.toLowerCase().trim().split(/\s+/).filter(word => word.length >= 2);
  
  if (words.length === 0) {
    return { primaryWord: '', secondaryWords: [] };
  }
  
  if (words.length === 1) {
    return { primaryWord: words[0], secondaryWords: [] };
  }
  
  // Sort by length (longest first) and prioritize product-specific terms
  const sortedWords = [...words].sort((a, b) => {
    // Prioritize product-specific terms over generic ones
    const aIsGeneric = ['primer', 'prim', 'wit', 'zwart', 'grijs'].includes(a.toLowerCase());
    const bIsGeneric = ['primer', 'prim', 'wit', 'zwart', 'grijs'].includes(b.toLowerCase());
    
    if (aIsGeneric && !bIsGeneric) return 1;
    if (!aIsGeneric && bIsGeneric) return -1;
    
    // If both are generic or both are specific, sort by length
    return b.length - a.length;
  });
  
  return {
    primaryWord: sortedWords[0],
    secondaryWords: sortedWords.slice(1)
  };
};

// Enhanced search with smart word prioritization and multi-tier strategy
export const searchInventory = async (debouncedSearch: string, selectedWarehouse: number | null) => {
  try {
    console.log('=== ENHANCED MULTI-TIER SEARCH START ===');
    console.log(`Search parameters: term="${debouncedSearch}", warehouse=${selectedWarehouse}`);
    
    // Always start with a reasonable limit
    let query = supabase
      .from('voorraad_live2')
      .select('*')
      .limit(1000);
    
    // Apply warehouse filter first if provided
    if (selectedWarehouse !== null) {
      console.log(`Applying warehouse filter for: ${selectedWarehouse}`);
      const warehouseStr = selectedWarehouse.toString();
      query = query.or(`magazijn.eq.${selectedWarehouse},magazijn.eq.${warehouseStr},magazijn.ilike.%${warehouseStr}%`);
    }

    let items: any[] = [];
    
    // Enhanced search strategy based on search term
    if (debouncedSearch && debouncedSearch.length >= 1) {
      const { primaryWord, secondaryWords } = prioritizeSearchWords(debouncedSearch);
      console.log(`Primary word: "${primaryWord}", Secondary words:`, secondaryWords);
      
      // Strategy 1: Search primarily on the most important word
      if (primaryWord.length >= 2) {
        console.log(`Strategy 1: Primary word search for "${primaryWord}"`);
        const primaryQuery = query.or(`product_naam.ilike.%${primaryWord}%,product_id.ilike.%${primaryWord}%,bc.ilike.%${primaryWord}%`);
        const { data: primaryResults } = await primaryQuery;
        
        if (primaryResults && primaryResults.length > 0) {
          console.log(`Strategy 1 found ${primaryResults.length} matches for primary word "${primaryWord}"`);
          items = primaryResults;
        } else {
          // Strategy 2: Fallback to broader search with partial primary word
          console.log(`Strategy 2: Partial primary word search`);
          if (primaryWord.length >= 4) {
            const partialWord = primaryWord.substring(0, Math.min(primaryWord.length - 1, 6));
            const partialQuery = query.or(`product_naam.ilike.%${partialWord}%,product_id.ilike.%${partialWord}%`);
            const { data: partialResults } = await partialQuery;
            
            if (partialResults && partialResults.length > 0) {
              console.log(`Strategy 2 found ${partialResults.length} partial matches for "${partialWord}"`);
              items = partialResults;
            }
          }
        }
        
        // Strategy 3: If still no results, try secondary words
        if (items.length === 0 && secondaryWords.length > 0) {
          console.log(`Strategy 3: Secondary word search`);
          const secondaryWord = secondaryWords[0];
          if (secondaryWord.length >= 3) {
            const secondaryQuery = query.or(`product_naam.ilike.%${secondaryWord}%,product_id.ilike.%${secondaryWord}%`);
            const { data: secondaryResults } = await secondaryQuery;
            
            if (secondaryResults && secondaryResults.length > 0) {
              console.log(`Strategy 3 found ${secondaryResults.length} matches for secondary word "${secondaryWord}"`);
              items = secondaryResults;
            }
          }
        }
        
        // Strategy 4: Ultra-broad fallback - any word matches
        if (items.length === 0) {
          console.log(`Strategy 4: Ultra-broad search`);
          const allWords = [primaryWord, ...secondaryWords];
          const broadConditions = allWords.map(word => 
            `product_naam.ilike.%${word}%`
          ).join(',');
          
          const broadQuery = query.or(broadConditions);
          const { data: broadResults } = await broadQuery;
          
          if (broadResults && broadResults.length > 0) {
            console.log(`Strategy 4 found ${broadResults.length} broad matches`);
            items = broadResults;
          }
        }
      }
    } else {
      // No search term, just apply warehouse filter
      const { data: warehouseResults, error } = await query;
      if (error) throw error;
      items = warehouseResults || [];
    }

    console.log(`Database query returned ${items.length} items for further processing`);

    // Get timestamp if available
    const lastUpdate = (items && items.length > 0 && items[0].timestamp_sync) || null;
    
    // Transform the results
    const formattedResults = formatInventoryItems(items || []);
    console.log('=== ENHANCED MULTI-TIER SEARCH COMPLETE ===');

    return { results: formattedResults, lastUpdate };
  } catch (error) {
    console.error('Error in enhanced multi-tier search:', error);
    return { results: [], lastUpdate: null };
  }
};

// Helper function to format inventory items consistently
export const formatInventoryItems = (items: any[]): InventoryItem[] => {
  return items.map(item => {
    // Handle magazijn value specifically - convert string with spaces to number
    let magazijn = item.magazijn;
    if (typeof magazijn === 'string') {
      const trimmed = magazijn.trim();
      if (/^\d+$/.test(trimmed)) {
        magazijn = Number(trimmed);
      }
    } else if (typeof magazijn === 'number') {
      // Already a number, use as is
      magazijn = magazijn;
    } else {
      // Default to 0 if null/undefined or other type
      magazijn = 0;
    }
    
    // Format the MOQ value - make sure it's a number or null
    let moq = item.moq;
    if (moq === undefined || moq === '') {
      moq = null;
    } else if (typeof moq === 'string') {
      moq = parseFloat(moq);
      if (isNaN(moq)) moq = null;
    }
    
    return {
      product_naam: String(item.product_naam || ''),
      product_id: String(item.product_id || ''),
      bc: String(item.bc || ''),
      locatie: String(item.locatie || ''),
      magazijn: magazijn,
      voorraad: Number(item.voorraad || 0),
      eenheid: String(item.eenheid || ''),
      moq: moq
    };
  });
};
